import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { FeatureFlagsGateway } from '@domain/models/feature-flags/gateway/feature-flags.gateway';
import { IFeatureFlags } from '@domain/interfaces/feature-flags/feature-flags.interface';
import { environment } from '@environments/environment';
import { resolveBrandDomain } from '@infraestructure/helpers/feature-flags/feature-flag-bypass.helper';
import { IS_PUBLIC } from '@infraestructure/helpers/auth/auth.interceptor';

interface IFeatureFlagsResponse {
  data: IFeatureFlags;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService extends FeatureFlagsGateway {
  private readonly flags$ = new BehaviorSubject<IFeatureFlags>({});
  public readonly flagsChanged$ = this.flags$.asObservable();
  // A cold page load (direct URL entry, browser refresh) runs featureFlagGuard before this
  // constructor's own refresh() below resolves. Without this gate, flagsChanged$'s synchronous
  // initial {} would make isEnabled() emit false immediately and the router would redirect away
  // before the real flag value ever arrives. shareReplay(1) lets every subscriber -- including
  // the guard, which subscribes well after this is created -- wait for that first result exactly
  // once, no matter how many times isEnabled()/getAllFlags() are called afterwards.
  private readonly ready$ = this.refresh().pipe(shareReplay(1));

  constructor(private http: HttpClient) {
    super();
    this.ready$.subscribe();
  }

  public refresh(): Observable<IFeatureFlags> {
    // IS_PUBLIC: fetched at app bootstrap, same as BrandService's brandresources call --
    // without it, auth.interceptor would try authSvc.refreshToken() first on every
    // unauthenticated visit (e.g. the login page), which this call must not depend on.
    return this.http
      .get<IFeatureFlagsResponse>(`${resolveBrandDomain(environment.memberPortalServiceURL)}/feature-flags`, {
        context: new HttpContext().set(IS_PUBLIC, true),
      })
      .pipe(
        map((response) => response.data),
        tap((flags) => this.flags$.next(flags)),
        catchError(() => of(this.flags$.value)),
      );
  }

  public isEnabled(key: string): Observable<boolean> {
    return this.ready$.pipe(
      switchMap(() => this.flagsChanged$),
      map((flags) => Boolean(flags[key])),
    );
  }

  public getAllFlags(): Observable<IFeatureFlags> {
    return this.ready$.pipe(switchMap(() => this.flagsChanged$));
  }
}
